import { useNavigate } from "react-router-dom"
import { Data2, DataTypeList3, useDataTypesHooks } from "../../../../../features/data-types"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { Action, Column, FieldType, FieldValue, FilterField2, FiltersOptions, PanelWrapper } from "../../../../../features/ui"
import { BookmarkButton } from "../../../../../features/bookmarks"
import { DataType, TableName } from "../../../../../tables"
import { Customer, useCustomerHooks, useSaleOperationHooks, CustomerDebt } from "../../../../sales"
import { Params } from "../../../../../state/reducers/types"
import { isDefined, round } from "../../../../../utils/typeUtils"
import { useState } from "react"

export const CreditDebtsList = () => {
    const [customersDebt, setCustomersDebt] = useState<CustomerDebt[]>([])
    const [skippedCount, setSkippedCount] = useState(0)

    const { loadCustomersBatch } = useCustomerHooks()
    const { getCustomersDebt } = useSaleOperationHooks()
    const { formatPrice } = useDataTypesHooks()
    const { getRoutePath } = useRoutesHooks()
    const navigate = useNavigate()

    const headerActions: Action[] = [
        {
            label: 'Cuentas Corrientes',
            kind: 'tertiary',
            href: getRoutePath(RoutePath.CREDIT_ACCOUNTS)
        }
    ]

    const filterFields: FilterField2[] = [
        {
            name: 'customerId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMERS,
            label: 'Cliente',
            space: 6,
        }
    ]

    const filtersOptions: FiltersOptions = {
        filtersKey: 'creditDebts',
        filterFields
    }

    const columns: Column[] = [
        {
            name: 'customerId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMERS,
            label: 'Cliente',
            sortKey: 'name'
        },
        {
            name: 'credit',
            type: FieldType.PRICE,
            label: 'Crédito Disponible',
            // Si el cliente se pasó del límite el disponible es negativo y se muestra en rojo
            render: (value?: FieldValue) => isDefined(value) ? (
                <span className={(value as number) < 0 ? 'text-danger' : 'text-success'}>{formatPrice(value as number)}</span>
            ) : <></>
        },
        {
            name: 'creditAccountDebt',
            type: FieldType.PRICE,
            label: 'Deuda Cta. Cte.'
        },
        {
            name: 'otherDebt',
            type: FieldType.PRICE,
            label: 'Otras Deudas'
        },
        {
            name: 'totalDebt',
            type: FieldType.PRICE,
            label: 'Deuda Total',
            render: (value?: FieldValue) => <span className="text-danger">{formatPrice(value as number)}</span>
        }
    ]

    const toRow = (customerDT: DataType): Data2 => {
        const customer = customerDT as Customer
        const customerDebt = customersDebt.find(debt => debt.customerId === customer.id)
        const creditAccountDebt = customerDebt?.creditAccountDebt || 0
        // El crédito disponible solo depende de la deuda de cuenta corriente
        const credit = customer.creditAccount ? round(customer.creditAccount.creditLimit - creditAccountDebt) : undefined
        return {
            ...customer,
            customerId: customer.id,
            creditAccountDebt,
            otherDebt: customerDebt?.otherDebt || 0,
            totalDebt: customerDebt?.totalDebt || 0,
            credit
        }
    }

    const onView = (id?: string) => {
        const options = { state: { customerId: id } }
        navigate(getRoutePath(RoutePath.CREDIT_ACCOUNTS), options)
    }

    const actions = async (): Promise<Action[]> => [
        { icon: 'eye-fill', kind: 'tertiary', tooltip: 'Ver Detalle', onClick: onView }
    ]
    
    const loadBatch = async (
        filtersData: Data2 = {},
        _batchSize?: number,
        startKey?: Params
    ) => {
        const { response, sales, hoards } = await loadCustomersBatch(filtersData, startKey)
        const { customersDebt: newCustomersDebt, skippedOperations } = getCustomersDebt([...sales, ...hoards], response.dataTypes)
        setCustomersDebt(newCustomersDebt)
        setSkippedCount(skippedOperations.length)
        const debtCustomers = newCustomersDebt
            .map(customerDebt => customerDebt.customer)
            .filter((customer): customer is Customer => !!customer)

        return {
            dataTypes: debtCustomers,
        }
    }

    return (
        <PanelWrapper
            title="Lista de Deudores"
            actions={headerActions}
            titleRight={<BookmarkButton routePath={`${RoutePath.CREDIT_ACCOUNTS}/${RoutePath.DEBTS}`} />}
        >
            <>
                {skippedCount > 0 && (
                    <p className="text-muted small">⚠ {skippedCount} operación(es) no se pudieron calcular y no están incluidas (ver consola)</p>
                )}
                <DataTypeList3
                    columns={columns}
                    filtersOptions={filtersOptions}
                    readOnly
                    toRow={toRow}
                    actions={actions}
                    loadBatch={loadBatch}
                    emptyMessage="No se encontraron deudores."
                />
            </>
        </PanelWrapper>
    )
}
